import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import useApi from "../../hooks/useApi";
import { useBranding } from "../../branding/BrandingProvider";

interface ProfilePhone {
  phone?: string | null;
  phoneVerified: boolean;
  /** NotificationChannel: 1 Email, 2 Text, 3 TextAndEmail. */
  channel?: number;
  /** A patient's choice to get sign-in codes by email rather than text. */
  mfaCodesByEmail?: boolean;
}

const CHANNELS = [
  { value: 1, label: "Email only" },
  { value: 2, label: "Text only" },
  { value: 3, label: "Email and text" },
];

/**
 * GET/POST /profile/sms-consent. `disclosure` is rendered verbatim; `version` goes back with a
 * consent. It also carries the caller's phone and its verification, because every audience can read
 * it — external users cannot read /profile.
 */
interface SmsConsentState {
  phone?: string | null;
  phoneVerified?: boolean;
  consented: boolean;
  consentedAt?: string | null;
  disclosure: string;
  version: string;
}

interface PhoneMfaSetupProps {
  /** Called after the phone number is successfully verified. */
  onVerified?: () => void | Promise<void>;
  /** Hide the "Verified" chip (e.g. in the forced setup gate where the user is unverified by definition). */
  hideVerifiedChip?: boolean;
  /**
   * Show the switch that turns texts off (and back on) for an already-verified number. Profile →
   * Preferences only; the forced setup gate has no business offering a way out of what it requires.
   */
  manageConsent?: boolean;
  /**
   * Offer the Email / Text / Both choice for notifications and reminders (SMS-5). Only where the
   * deployment texts them (IdentityModel.SmsNotificationsAvailable), and only once the number is
   * verified and consented to.
   */
  offerChannel?: boolean;
  /**
   * Offer patients the choice of how sign-in codes arrive: text or email (Derek, 2026-10-21).
   * Separate from `offerChannel`, so a patient can keep appointment texts and still get codes by
   * email. Patients only — staff and providers must use their phone, and the server refuses them.
   */
  offerCodeChoice?: boolean;
  /**
   * One column, full width, in the order the user acts: number, consent, Send code — for the
   * narrow card of the forced setup gate. Profile keeps the compact field-and-button row.
   */
  stacked?: boolean;
}

/**
 * Self-contained phone-number verification sub-flow used both in Profile → Preferences and in the
 * forced post-login MFA-setup gate. Loads the user's current phone from /profile, sends a code via
 * /profile/phone/send-verification, and confirms it via /profile/phone/verify. Once verified, the
 * backend marks the phone verified (which clears MfaSetupRequired on the next /identity), and
 * onVerified() fires so the parent can react (e.g. refresh identity / advance the gate).
 *
 * The code is itself a text, so it is only sent once the user ticks the consent box — unchecked by
 * default, beside the server's own disclosure and the brand's Terms and Privacy links. That box is
 * the opt-in toll-free SMS verification reviews (reason 30510), and the server refuses the send
 * without it.
 */
export default function PhoneMfaSetup({
  onVerified,
  hideVerifiedChip,
  manageConsent,
  offerChannel,
  offerCodeChoice,
  stacked,
}: PhoneMfaSetupProps) {
  const api = useApi();
  const brand = useBranding();
  const { enqueueSnackbar } = useSnackbar();

  const [phone, setPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [consent, setConsent] = useState<SmsConsentState | null>(null);
  const [consentLoadFailed, setConsentLoadFailed] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);
  const [channel, setChannel] = useState(1);
  const [savingChannel, setSavingChannel] = useState(false);
  const [codesByEmail, setCodesByEmail] = useState(false);
  const [savingCodes, setSavingCodes] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get<SmsConsentState>("/profile/sms-consent");
        setConsent(data);
        setPhone(data.phone ?? "");
        setPhoneVerified(!!data.phoneVerified);
      } catch {
        // Fatal for sending a code: without the disclosure there is nothing to agree to.
        setConsentLoadFailed(true);
      }
      // The notification channel and the sign-in-code choice live on the profile, and only patients
      // make either (offerChannel / offerCodeChoice).
      if (offerChannel || offerCodeChoice) {
        try {
          const { data } = await api.get<ProfilePhone>("/profile");
          setChannel(data.channel ?? 1);
          setCodesByEmail(!!data.mfaCodesByEmail);
        } catch {
          // Non-fatal: the choices show their defaults until they load.
        }
      }
    };
    load();
  }, [api, offerChannel, offerCodeChoice]);

  const handleSendCode = async () => {
    if (!consent) return;
    setSending(true);
    try {
      const { data } = await api.post<{
        challengeToken: string;
        destination: string;
      }>("/profile/phone/send-verification", {
        phone,
        smsConsent: agreed,
        disclosureVersion: consent.version,
      });
      setChallengeToken(data.challengeToken);
      setPhoneVerified(false);
      setConsent({ ...consent, consented: true });
      enqueueSnackbar(`We sent a code to ${data.destination}.`, {
        variant: "info",
      });
    } catch (error) {
      let message = "Couldn't send a verification code.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!challengeToken) return;
    setVerifying(true);
    try {
      await api.post("/profile/phone/verify", { challengeToken, code });
      setPhoneVerified(true);
      setChallengeToken(null);
      setCode("");
      enqueueSnackbar("Your phone number is verified.", {
        variant: "success",
      });
      await onVerified?.();
    } catch (error) {
      let message = "Incorrect or expired code.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setVerifying(false);
    }
  };

  const handleSetConsent = async (next: boolean) => {
    if (!consent) return;
    setSavingConsent(true);
    try {
      const { data } = await api.post<SmsConsentState>("/profile/sms-consent", {
        consent: next,
        disclosureVersion: consent.version,
      });
      setConsent(data);
      setAgreed(false);
      enqueueSnackbar(
        next
          ? "Text messages are on."
          : "Text messages are off. Sign-in codes will be emailed to you.",
        { variant: "success" },
      );
    } catch (error) {
      let message = "Couldn't update your text-message setting.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSavingConsent(false);
    }
  };

  const handleChannel = async (next: number) => {
    const previous = channel;
    setChannel(next);
    setSavingChannel(true);
    try {
      await api.post("/profile/channel", { channel: next });
      enqueueSnackbar("Notification preference saved.", { variant: "success" });
    } catch (error) {
      setChannel(previous);
      let message = "Couldn't save your notification preference.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSavingChannel(false);
    }
  };

  const handleCodesByEmail = async (byEmail: boolean) => {
    const previous = codesByEmail;
    setCodesByEmail(byEmail);
    setSavingCodes(true);
    try {
      await api.post("/profile/mfa-codes", { byEmail });
      enqueueSnackbar("Sign-in code preference saved.", { variant: "success" });
    } catch (error) {
      setCodesByEmail(previous);
      let message = "Couldn't save your sign-in code preference.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSavingCodes(false);
    }
  };

  const consentBox = consent && (
    <Box sx={{ mt: 1.5 }}>
      <FormControlLabel
        sx={{ alignItems: "flex-start", mr: 0 }}
        control={
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            sx={{ pt: 0.25 }}
          />
        }
        label={
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {consent.disclosure}
          </Typography>
        }
      />
      {(brand.termsUrl || brand.privacyUrl) && (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", ml: 4, mt: 0.5 }}
        >
          {brand.termsUrl && (
            <Link href={brand.termsUrl} target="_blank" rel="noopener">
              Terms
            </Link>
          )}
          {brand.termsUrl && brand.privacyUrl && " · "}
          {brand.privacyUrl && (
            <Link href={brand.privacyUrl} target="_blank" rel="noopener">
              Privacy Policy
            </Link>
          )}
        </Typography>
      )}
    </Box>
  );

  const phoneField = (
    <TextField
      label="Mobile number"
      size={stacked ? "medium" : "small"}
      fullWidth
      value={phone}
      onChange={(e) => {
        setPhone(e.target.value);
        setPhoneVerified(false);
        setChallengeToken(null);
      }}
      placeholder="(555) 123-4567"
      slotProps={{ htmlInput: { inputMode: "tel", autoComplete: "tel" } }}
    />
  );

  const sendButton = (
    <Button
      variant={stacked ? "contained" : "outlined"}
      onClick={handleSendCode}
      disabled={sending || !phone || phoneVerified || !consent || !agreed}
      sx={stacked ? { mt: 2 } : { whiteSpace: "nowrap", mt: 0.25 }}
      fullWidth={stacked}
      size={stacked ? "large" : "medium"}
    >
      {phoneVerified
        ? "Verified"
        : sending
          ? "Sending…"
          : challengeToken
            ? "Resend"
            : "Send code"}
    </Button>
  );

  return (
    <Box sx={{ maxWidth: stacked ? undefined : 480 }}>
      <Typography variant="subtitle1" gutterBottom>
        Phone number
        {phoneVerified && !hideVerifiedChip && (
          <Chip label="Verified" color="success" size="small" sx={{ ml: 1 }} />
        )}
      </Typography>

      {stacked ? (
        <Box sx={{ mt: 1 }}>{phoneField}</Box>
      ) : (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          {phoneField}
          {sendButton}
        </Box>
      )}

      {!phoneVerified && consentBox}
      {stacked && sendButton}
      {!phoneVerified && consentLoadFailed && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Couldn't load the text-message terms. Reload the page to try again.
        </Typography>
      )}

      {challengeToken && !phoneVerified && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: stacked ? "column" : "row",
            gap: stacked ? 2 : 1,
            alignItems: stacked ? "stretch" : "center",
          }}
        >
          <TextField
            label="Verification code"
            size="small"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                autoComplete: "one-time-code",
                maxLength: 6,
              },
            }}
          />
          <Button
            variant="contained"
            size={stacked ? "large" : "medium"}
            onClick={handleVerify}
            disabled={verifying || code.length !== 6}
          >
            {verifying ? "Verifying…" : "Verify"}
          </Button>
        </Box>
      )}

      {manageConsent && phoneVerified && consent && (
        <Box sx={{ mt: 2 }}>
          {consent.consented ? (
            <>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                You've agreed to receive text messages at this number. Reply
                STOP to any message, or turn them off here, at any time.
              </Typography>
              {offerCodeChoice && (
                <FormControl sx={{ mt: 2, display: "block" }}>
                  <FormLabel id="sign-in-code-label">Send my sign-in codes by</FormLabel>
                  <RadioGroup
                    aria-labelledby="sign-in-code-label"
                    value={codesByEmail ? "email" : "text"}
                    onChange={(e) => handleCodesByEmail(e.target.value === "email")}
                  >
                    <FormControlLabel
                      value="text"
                      control={<Radio size="small" disabled={savingCodes} />}
                      label="Text message"
                    />
                    <FormControlLabel
                      value="email"
                      control={<Radio size="small" disabled={savingCodes} />}
                      label="Email"
                    />
                  </RadioGroup>
                </FormControl>
              )}
              {offerChannel && (
                <FormControl sx={{ mt: 2, display: "block" }}>
                  <FormLabel id="notification-channel-label">
                    Send notifications and appointment reminders by
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="notification-channel-label"
                    value={channel}
                    onChange={(e) => handleChannel(Number(e.target.value))}
                  >
                    {CHANNELS.map((c) => (
                      <FormControlLabel
                        key={c.value}
                        value={c.value}
                        control={
                          <Radio size="small" disabled={savingChannel} />
                        }
                        label={c.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
              <Button
                variant="text"
                color="inherit"
                sx={{ mt: 1 }}
                onClick={() => handleSetConsent(false)}
                disabled={savingConsent}
              >
                Stop text messages
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Text messages are off. Sign-in codes are emailed to you.
              </Typography>
              {consentBox}
              <Button
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={() => handleSetConsent(true)}
                disabled={savingConsent || !agreed}
              >
                Turn on text messages
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
